/**
 * Titolo pagina dinamico per pagina e lingua.
 *
 * - Rotte STATICHE: risolte centralmente da `staticTitleForPath` (chiamata in
 *   LocaleLayout), che riconosce la pagina dallo slug in QUALSIASI lingua.
 * - Rotte di DETTAGLIO (prodotto/articolo): il titolo lo imposta la pagina dopo
 *   il fetch, con `usePageTitle(nome)` (il nome arriva già localizzato dall'API).
 *
 * **Il titolo arriva già corretto dal server**, che lo inietta in `index.html`.
 * Questo modulo lo mantiene tale durante la navigazione interna, dove il server
 * non viene più interpellato: se qui la pagina non viene riconosciuta, il
 * titolo buono del server viene sostituito da un generico «Granborgo».
 *
 * È quello che succedeva: lo switch confrontava gli slug **solo in italiano**,
 * quindi `/en/about-us`, `/de/ueber-uns`, `/de/kollektionen`, `/en/tax-benefits`
 * e tutti gli Impressum cadevano nel ramo predefinito. Ora la risoluzione passa
 * da `pageForSlug()`, che è la stessa funzione usata dal router e conosce tutte
 * e tre le lingue — una sola tabella di slug, che non può divergere.
 */
import { useEffect } from "react";
import type { TFunction } from "i18next";
import { pageForSlug } from "./paths";

export const BRAND = "Granborgo";

/** Titolo completo della scheda: "Nome - Granborgo" (o solo il brand se assente). */
export function makeTitle(name: string | null | undefined): string {
  return name ? `${name} - ${BRAND}` : BRAND;
}

/** Chiave di traduzione del titolo, per ogni pagina del sito. */
const META_KEY: Record<string, string> = {
  collections: "meta.collections",
  about: "meta.about",
  contact: "meta.contact",
  fiscal: "meta.fiscalita",
  privacy: "meta.privacy",
  impressum: "meta.impressum",
  blog: "meta.blog",
};

/** Rotte funzionali: non sono localizzate e hanno lo stesso segmento ovunque. */
const FUNCTIONAL_KEY: Record<string, string> = {
  account: "meta.account",
  login: "meta.login",
  signup: "meta.signup",
  auth: "meta.verify",
};

/**
 * Titolo per le rotte statiche a partire dal pathname. Ritorna `null` per le
 * rotte di dettaglio (prodotto/articolo del blog): lì il titolo lo imposta la
 * pagina via `usePageTitle` dopo il fetch, quando conosce il nome.
 */
export function staticTitleForPath(pathname: string, t: TFunction): string | null {
  const parts = pathname.split("/").filter(Boolean); // es. ["de", "ueber-uns"]
  const seg = parts[1] || ""; // primo segmento dopo il locale
  const sub = parts[2] || "";

  if (!seg) return `${BRAND} - ${t("meta.homeTagline")}`;

  if (seg === "checkout") {
    if (sub === "success") return makeTitle(t("meta.checkoutSuccess"));
    if (sub === "cancel") return makeTitle(t("meta.checkoutCancel"));
    return makeTitle(t("meta.checkout"));
  }
  const funzionale = FUNCTIONAL_KEY[seg];
  if (funzionale) return makeTitle(t(funzionale));

  // Lo slug viene risolto nella pagina che rappresenta, in ogni lingua.
  // `hasSub` distingue la lista collezioni dal dettaglio prodotto, che in
  // inglese e tedesco condividono lo stesso slug.
  const page = pageForSlug(seg, Boolean(sub));
  if (!page) return null;

  // Dettaglio prodotto o articolo: il nome lo conosce solo la pagina.
  if (page === "product") return null;
  if (page === "blog" && sub) return null;

  const key = META_KEY[page];
  return key ? makeTitle(t(key)) : null;
}

/** Imposta `document.title = "Nome - Granborgo"` quando `name` è disponibile. */
export function usePageTitle(name: string | null | undefined): void {
  useEffect(() => {
    if (name) document.title = makeTitle(name);
  }, [name]);
}
